import { GoogleLoginButton, type GoogleLoginTexts } from '@tucan/login'
import { getPublicEnv } from '@tucan/utils'
import { captureException } from '@tucan/utils/error-tracking'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoginApi } from 'hooks/use.login.api'
import { useAppDispatch, useAppSelector } from 'store'
import { triggerLogin } from 'store/auth'
import { useRegisterActivity } from '../../hooks/use.register.activity'

interface Props {
  onSubmitting?: (submitting: boolean) => void
  onSuccess?: () => void
  disabled?: boolean
  onClick?: (cb: () => void) => void
}

/** The ui's texts of `@tucan/login`'s Google button. */
export const useGoogleLoginTexts = (): GoogleLoginTexts => {
  const { t } = useTranslation()

  return {
    failed: t('google-login-failed', 'Login with google failed'),
    label: t('login-google', 'Login with Google'),
    loggedIn: t('login-successfully', 'You are now logged in!'),
    unavailable: t('can-not-login-with-google', 'Cannot use Google Login'),
  }
}

/**
 * `@tucan/login`'s Google button with the ui's side effects: marks the store as signed in and
 * records the `LOGIN` activity. Renders nothing without `NEXT_PUBLIC_GOOGLE_CLIENT_ID`, and is
 * disabled while signed in.
 */
export const LoginWithGoogleButton: FC<Props> = props => {
  const clientId = getPublicEnv('GOOGLE_CLIENT_ID')
  const authenticated = useAppSelector(state => state.auth.authenticated)
  const dispatch = useAppDispatch()
  const registerActivity = useRegisterActivity()
  const api = useLoginApi()
  const texts = useGoogleLoginTexts()

  if (!clientId) {
    return null
  }

  return (
    <GoogleLoginButton
      clientId={clientId}
      api={api}
      texts={texts}
      disabled={props.disabled || authenticated}
      onClick={props.onClick}
      onSubmitting={props.onSubmitting}
      onError={captureException}
      onSuccess={() => {
        dispatch(triggerLogin())
        void registerActivity('LOGIN', undefined, { with: 'google' })
        props.onSuccess?.()
      }}
    />
  )
}
